<section class="horizon-card">
  <header class="horizon-card__head">
    <div>
      <h3 class="horizon-card__title">Shared With Buyer</h3>
      <p class="horizon-card__sub">{{ summaryText }}</p>
    </div>
    <div class="horizon-card__actions">
      <button type="button" class="horizon__link" *ngIf="summary.properties + summary.documents > 3 || showAll" (click)="toggleViewAll()">
        {{ showAll ? 'Show less' : 'View All' }}
      </button>
      <button
        type="button"
        class="_primary-btn horizon__icon-btn"
        [disabled]="!canShare"
        [attr.title]="canShare ? null : 'This buyer needs a Horizon invitation before you can share with them.'"
        (click)="openDialog()">
        Share New Items <i class="pi pi-arrow-right" aria-hidden="true"></i>
      </button>
    </div>
  </header>

  <div class="horizon__state" *ngIf="isLoading"><i class="pi pi-spin pi-spinner" aria-hidden="true"></i> Loading shared items…</div>
  <div class="horizon__state horizon__state--error" *ngIf="hasLoadError">
    Shared items could not be loaded.
    <button type="button" class="_outline-btn" (click)="load()">Retry</button>
  </div>
  <p class="horizon__empty" *ngIf="isEmpty">Use “Share New Items” to send lots, floor plans or documents. The buyer is notified in Horizon.</p>

  <div class="sh-list" *ngIf="shares.length">
    <article class="sh-card" *ngFor="let share of shares; trackBy: trackByShare">
      <div class="sh-card__media">
        <span class="sh-tag" [class.sh-tag--new]="share.status === 'new'">
          <i [class]="share.status === 'new' ? 'fa-solid fa-circle-plus' : 'fa-solid fa-circle-check'" aria-hidden="true"></i>
          {{ share.status === 'new' ? 'New' : 'Viewed' }}
        </span>
        <!-- Each picture opens full size in a new tab. -->
        <ng-container *ngIf="share.type === 'lot' && share.lot; else docMedia">
          <button
            type="button"
            class="sh-card__photo sh-card__photo--contain sh-card__zoom"
            *ngIf="lotImage(share) as image; else noLotImage"
            [attr.aria-label]="'Open the image of ' + share.lot.name"
            (click)="openImage(image)">
            <img [src]="image" [alt]="share.lot.name" loading="lazy" (error)="markBroken(share, 'image')" />
          </button>
          <ng-template #noLotImage>
            <span class="sh-card__photo sh-card__photo--empty">
              <i class="fa-regular fa-image" aria-hidden="true"></i>
              <small>No floor plan linked</small>
            </span>
          </ng-template>
          <button
            type="button"
            class="sh-card__plan sh-card__zoom"
            *ngIf="share.lot.floorplan_image && share.lot.floorplan_image !== lotImage(share)"
            aria-label="Open the floor plan"
            (click)="openImage(share.lot.floorplan_image)">
            <img [src]="share.lot.floorplan_image" alt="Floor plan" loading="lazy" />
          </button>
        </ng-container>
        <ng-template #docMedia>
          <button
            type="button"
            class="sh-card__photo sh-card__zoom"
            *ngIf="docThumbnail(share) as thumbnail; else noDocThumbnail"
            [attr.aria-label]="'Open the preview of ' + share.document?.name"
            (click)="openImage(docPreview(share))">
            <img [src]="thumbnail" [alt]="share.document?.name" loading="lazy" (error)="markBroken(share, 'thumbnail')" />
          </button>
          <ng-template #noDocThumbnail>
            <span class="sh-card__photo sh-card__photo--empty sh-card__photo--file">
              <i class="fa-regular fa-file-lines" aria-hidden="true"></i>
              <small>{{ share.document?.file_type || 'File' | uppercase }}</small>
            </span>
          </ng-template>
        </ng-template>
      </div>

      <div class="sh-card__body">
        <ng-container *ngIf="share.type === 'lot' && share.lot; else docBody">
          <strong class="sh-card__name">{{ share.lot.name }}</strong>
          <span class="sh-card__specs">
            <span *ngIf="share.lot.bed !== null && share.lot.bed !== undefined"
              ><i class="fa-solid fa-bed" aria-hidden="true"></i> {{ share.lot.bed }}</span
            >
            <span *ngIf="share.lot.bath !== null && share.lot.bath !== undefined"
              ><i class="fa-solid fa-bath" aria-hidden="true"></i> {{ share.lot.bath }}</span
            >
            <span *ngIf="share.lot.car !== null && share.lot.car !== undefined"
              ><i class="fa-solid fa-car" aria-hidden="true"></i> {{ share.lot.car }}</span
            >
          </span>
          <small class="sh-card__areas" *ngIf="share.lot.total_area">
            Internal {{ share.lot.internal_area }} m² | Balcony {{ share.lot.balcony_area }} m² | Total {{ share.lot.total_area }} m²
          </small>
        </ng-container>
        <ng-template #docBody>
          <strong class="sh-card__name">{{ share.document?.name }}</strong>
          <small class="sh-card__areas"
            >{{ share.document?.file_type | uppercase
            }}<ng-container *ngIf="share.document?.size_label"> · {{ share.document.size_label }}</ng-container
            ><ng-container *ngIf="share.download_count"> · downloaded {{ share.download_count }}×</ng-container></small
          >
        </ng-template>
        <small class="sh-card__message" *ngIf="share.message" [title]="share.message">“{{ share.message }}”</small>
      </div>

      <footer class="sh-card__foot">
        <span class="sh-card__who">
          <span class="sh-avatar">
            <img *ngIf="share.shared_by.avatar" [src]="share.shared_by.avatar" alt="" />
            <ng-container *ngIf="!share.shared_by.avatar">{{ initials(share.shared_by.name) }}</ng-container>
          </span>
          <small class="sh-card__dates">
            <span>Shared {{ formatShared(share.shared_at) }}</span>
            <span *ngIf="share.viewed_at; else notViewed">
              Viewed {{ formatShared(share.viewed_at) }}<ng-container *ngIf="share.view_count > 1"> ({{ share.view_count }} times)</ng-container>
            </span>
            <ng-template #notViewed><span class="sh-card__unseen">Not viewed yet</span></ng-template>
          </small>
        </span>
        <span class="sh-card__actions">
          <button
            type="button"
            class="icon_btn sh-card__icon"
            *ngIf="share.type === 'lot'"
            aria-label="View details"
            pTooltip="View details"
            tooltipPosition="top"
            (click)="openLot(share)">
            <i class="pi pi-eye" aria-hidden="true"></i>
          </button>
          <!-- QA: no "⋮" menu on the card; Revoke is its own icon button that still asks to confirm. -->
          <button
            type="button"
            class="icon_btn sh-card__icon sh-card__icon--danger"
            aria-label="Revoke"
            pTooltip="Revoke: stop the buyer opening this item"
            tooltipPosition="top"
            (click)="askRevoke(share)">
            <i class="pi pi-ban" aria-hidden="true"></i>
          </button>
        </span>
      </footer>
    </article>
  </div>

  <div class="sh-more" *ngIf="showAll && hasMore">
    <button type="button" class="_outline-btn" [disabled]="isLoadingMore" (click)="loadMore()">{{ isLoadingMore ? 'Loading…' : 'Load more' }}</button>
  </div>
</section>

<app-horizon-share-dialog [(visible)]="isDialogOpen" [leadId]="leadId" [projectId]="projectId" (shared)="load()"></app-horizon-share-dialog>
